import React, { useContext, useEffect } from 'react';

import AppContext from 'lane-shared/contexts/AppContext';
import {
  OnboardingContextProvider,
  OnboardingData,
} from 'lane-shared/contexts/OnboardingContext';
import { imageUrl } from 'lane-shared/helpers/formatters';
import { useAuth0Enabled } from 'lane-shared/hooks/auth0/useAuth0Enabled';
import { useInviteContext } from 'lane-shared/contexts/InviteContext';

import { UserIdentificationAndOnboardingTemplate as Template } from 'components/pageTemplate/UserIdentificationAndOnboarding';
import { Loading } from 'design-system-web/components/Loading/Loading';
import { useFlag, useUserDataContext } from 'lane-shared/hooks';
import { useWorkplaceOnboardingEnabled } from 'lane-shared/hooks/onboarding/useWorkplaceOnboardingEnabled';
import { FeatureFlag } from 'constants-flags';

import { OnboardingSteps } from './OnboardingSteps';
import { useAuthClearSession } from './useAuthClearSession';

const MESSAGES = {
  notAvailable: 'Not available',
};

export type UserOnboardingData = OnboardingData;

type Props = {
  onOnboarding: (data: OnboardingData) => Promise<void>;
  onOnboardingError: (error: Error) => Promise<string>;
};

export const Onboarding = ({ onOnboarding, onOnboardingError }: Props) => {
  const { whitelabel } = useContext(AppContext);
  const { user } = useUserDataContext();
  const isWorkplaceOnboardingEnabled = useWorkplaceOnboardingEnabled();

  const isAuth0Enabled = useAuth0Enabled();
  const { setEmail, building, company, userName, loading } = useInviteContext();
  const authClearSession = useAuthClearSession();

  const companySelectionFFOn = useFlag(
    FeatureFlag.CompanySelectionRequiredDuringSignup,
    false
  );
  const companySelectionRequired =
    whitelabel.settings?.companySelectionRequiredDuringSignup && companySelectionFFOn;

  useEffect(() => {
    if (user?.profile?.email && isWorkplaceOnboardingEnabled) {
      setEmail(user.profile.email);
    }
  }, [user?.profile?.email]);

  useEffect(() => {
    function handleBeforeUnload(_e: BeforeUnloadEvent) {
      // beforeunload doesn't expose which element triggered navigation.
      // document.activeElement is the best available heuristic to detect
      // mailto: link clicks and avoid clearing the session unnecessarily.
      if (companySelectionRequired) {
        const activeEl = document.activeElement as HTMLAnchorElement | null;

        if (activeEl?.tagName === 'A' && activeEl.href?.startsWith('mailto:')) {
          return;
        }
      }

      authClearSession();
    }

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [authClearSession, companySelectionRequired]);

  const handleOnboardingComplete = async (data: UserOnboardingData) => {
    await onOnboarding(data);
  };

  if (!isAuth0Enabled) {
    return <div>{MESSAGES.notAvailable}</div>;
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <OnboardingContextProvider
      defaultData={{ userName, building, company }}
      asyncOnComplete={handleOnboardingComplete}
      asyncOnError={onOnboardingError}
    >
      <Template
        logo={imageUrl(whitelabel.profile.logo)}
        name={whitelabel.profile.name}
      >
        <OnboardingSteps />
      </Template>
    </OnboardingContextProvider>
  );
};
